import { logger } from './logger';

/**
 * Resource hints for critical third-party domains and API endpoints
 * Helps improve LCP by establishing early connections
 */

interface ResourceHint {
  href: string;
  rel: 'preconnect' | 'dns-prefetch' | 'prefetch' | 'preload';
  crossorigin?: boolean;
  as?: string;
}

// Critical domains that should be preconnected
const CRITICAL_DOMAINS: string[] = [
  // CDN domains
  'https://cdn.bunny.net',
  'https://fonts.googleapis.com',
  'https://fonts.gstatic.com',
];

// Domains for DNS prefetch (less critical)
const DNS_PREFETCH_DOMAINS: string[] = [
  'https://connect.facebook.net',
  'https://www.facebook.com',
  'https://platform.twitter.com',
];

// Create and append resource hint link
const createResourceHint = (hint: ResourceHint): void => {
  // Check if hint already exists
  const existing = document.querySelector(`link[rel="${hint.rel}"][href="${hint.href}"]`);
  if (existing) {
    return;
  }

  const link = document.createElement('link');
  link.rel = hint.rel;
  link.href = hint.href;

  if (hint.crossorigin) {
    link.crossOrigin = 'anonymous';
  }

  if (hint.as) {
    (link as HTMLLinkElement & { as?: string }).as = hint.as;
  }

  document.head.appendChild(link);
};

// Add preconnect hints for critical domains
export const addPreconnectHints = (): void => {
  CRITICAL_DOMAINS.forEach((domain) => {
    createResourceHint({
      href: domain,
      rel: 'preconnect',
      crossorigin: true,
    });
  });
};

// Add DNS prefetch hints for less critical domains
export const addDNSPrefetchHints = (): void => {
  DNS_PREFETCH_DOMAINS.forEach((domain) => {
    createResourceHint({
      href: domain,
      rel: 'dns-prefetch',
    });
  });
};

// Prefetch critical API endpoints
export const prefetchCriticalAPIs = (endpoints: string[]): void => {
  endpoints.forEach((endpoint) => {
    createResourceHint({
      href: endpoint,
      rel: 'prefetch',
      as: 'fetch',
    });
  });
};

// Initialize all resource hints
export const initializeResourceHints = (): void => {
  // Add preconnect hints immediately
  addPreconnectHints();

  // Add DNS prefetch hints
  addDNSPrefetchHints();

  // Add critical API prefetch hints
  const criticalAPIs: string[] = [
    // Add your critical API endpoints here
    // '/api/banners',
    // '/api/categories',
  ];

  if (criticalAPIs.length > 0) {
    prefetchCriticalAPIs(criticalAPIs);
  }

  logger.log('✅ Resource hints initialized for improved LCP');
};
